import type { ActiveSessionData } from "@sukha/timer-core";

/**
 * Sync heartbeats rewrite the `["activeSession"]` cache entry every 30-60s,
 * but most of the time the only change is `durationSeconds` advancing by
 * exactly the wall-clock time since the previous write — no information the
 * UI doesn't already have. Every identity change re-renders every subscriber,
 * which caused multi-second main-thread stalls in long sessions.
 *
 * This module decides whether a sync result carries news. A heartbeat is
 * "no news" when every stable field matches the previous cache entry and the
 * new duration is within tolerance of the wall-clock extrapolation from the
 * last recorded server observation. Real events — pause/resume, timer edits
 * from another device, post-sleep drift, session end — always pass through.
 *
 * The extrapolation is keyed by session id, NOT by cache-object identity:
 * TanStack structural sharing stores a merged copy of whatever a queryFn or
 * `setQueryData` updater returns, so an identity-keyed anchor (e.g. a WeakMap
 * on the emitted object) is silently lost after the first news write and every
 * later heartbeat would count as news again.
 *
 * It also tracks the latest server-observed elapsed time (even for skipped
 * writes) so callers that need fresh elapsed seconds (throttle decisions,
 * labels) can read it imperatively instead of subscribing to the cache.
 */

/**
 * Max drift (seconds) between the server duration and the local wall-clock
 * extrapolation before a heartbeat counts as news. Covers scheduler jitter
 * and network latency; real sleep/suspend gaps far exceed it.
 */
const NO_NEWS_DRIFT_TOLERANCE_SECONDS = 10;

interface ServerObservation {
  sessionId: string;
  durationSeconds: number;
  isPaused: boolean;
  observedAtMs: number;
}

let lastObservation: ServerObservation | null = null;

/**
 * Records the server-reported elapsed time from a sync result, whether or not
 * the cache write was skipped.
 *
 * Call this AFTER {@link isNoNewsActiveSessionSync}: the no-news check
 * extrapolates from the previous observation, so recording first would make
 * every sync compare against itself and always pass.
 */
export function recordActiveSessionObservation(
  data: ActiveSessionData,
  nowMs: number = Date.now(),
): void {
  if (!data.session?.id) return;
  lastObservation = {
    sessionId: data.session.id,
    durationSeconds: data.durationSeconds ?? 0,
    isPaused: data.isPaused,
    observedAtMs: nowMs,
  };
}

/** Clears the tracked observation (test isolation, sign-out teardown). */
export function resetActiveSessionFreshness(): void {
  lastObservation = null;
}

/**
 * Latest known focus-elapsed seconds for `sessionId`, extrapolated to now.
 * Returns null when the tracked observation is missing or for another
 * session. Read this in callbacks and render paths that need fresh elapsed
 * time without subscribing to per-heartbeat cache writes.
 */
export function getLastKnownSessionElapsedSeconds(
  sessionId: string | null | undefined,
  nowMs: number = Date.now(),
): number | null {
  if (!lastObservation || !sessionId) return null;
  if (lastObservation.sessionId !== sessionId) return null;
  if (lastObservation.isPaused) return lastObservation.durationSeconds;
  return (
    lastObservation.durationSeconds +
    Math.max(0, Math.floor((nowMs - lastObservation.observedAtMs) / 1000))
  );
}

/**
 * True when `next` differs from `prev` only by `durationSeconds` advancing as
 * the wall clock predicts — i.e. the sync carries no news and the previous
 * cache object can keep its identity. A sync with no prior observation for
 * the session (first sync, or an observation from another session) is always
 * news.
 */
export function isNoNewsActiveSessionSync(
  prev: ActiveSessionData | null,
  next: ActiveSessionData | null,
  nowMs: number = Date.now(),
): boolean {
  if (!prev?.session?.id || !next?.session?.id) return false;
  if (prev.session.id !== next.session.id) return false;
  if (prev.isPaused !== next.isPaused) return false;
  if (prev.currentIntervalStartTime !== next.currentIntervalStartTime) {
    return false;
  }
  if (prev.session.tempFocusTime !== next.session.tempFocusTime) return false;
  if (prev.session.tempBreakTime !== next.session.tempBreakTime) return false;
  if (prev.session.endedAt !== next.session.endedAt) return false;

  if (!lastObservation || lastObservation.sessionId !== next.session.id) {
    return false;
  }

  const expected = lastObservation.isPaused
    ? lastObservation.durationSeconds
    : lastObservation.durationSeconds +
      (nowMs - lastObservation.observedAtMs) / 1000;
  return (
    Math.abs((next.durationSeconds ?? 0) - expected) <=
    NO_NEWS_DRIFT_TOLERANCE_SECONDS
  );
}
